/**
 * CallSheet
 *
 * A sheet over the call (LC-1): the call canvas, top radius 34, the dismiss
 * control on the leading edge of its bar and the title centred (SH-8), the
 * same shell as CallInviteModal. Backdrop tap and Escape dismiss as the
 * button does. Rendered inside the call, so it sits above the tiles and the
 * pill and below toasts.
 */

import { useEffect, type ReactNode } from 'react';

export default function CallSheet({
  title, onClose, busy = false, dismissLabel = 'Cancel', children,
}: {
  title?: string;
  onClose: () => void;
  busy?: boolean;
  dismissLabel?: 'Cancel' | 'Close';
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div
      className="fixed inset-0 z-60 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-[34px] bg-call-canvas"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* The title keeps clear of Cancel on both sides, and a long question wraps rather than runs into it. */}
        <div className="relative flex min-h-14 flex-shrink-0 items-center justify-center px-[88px] py-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="ios-press absolute left-2 flex h-11 items-center rounded-full px-3 text-[17px] leading-[22px] text-white disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            {dismissLabel}
          </button>
          {title && <h2 className="text-balance text-center text-[17px] leading-[22px] font-semibold text-white">{title}</h2>}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4">{children}</div>
      </div>
    </div>
  );
}
